import { Component, Input, Output, EventEmitter } from '@angular/core';
import { OrganizationEntityDto } from 'app/@types/dto/organization-entity-dto.model';
import { SelectChangeEvent } from 'primeng/select';
import { TreeNode } from 'primeng/api';
import { TagEntitiesDto } from 'app/@types/dto/tag-entities-dto.model';
import { TagFilterMode } from 'app/@types/shared/results-visualization';
import { UserInfoListEntry } from 'app/main/dedicated_admin/home/home.component';

export type EntitySelectionMode = 'single' | 'multiple';

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
    standalone: false,
})
export class HeaderComponent {
    // === LEFT SIDE ===
    @Input() title: string = '';
    @Input() description: string = '';
    // A working page (a questionnaire): no description and tighter spacing, so the page's content
    // gets the height.
    @Input() compact = false;

    @Input() entityOptions?: OrganizationEntityDto[];
    @Input() yearOptions?: number[];
    @Input() filtersOptions?: string[];
    @Input() tagsTreeOptions?: TreeNode<TagEntitiesDto>[];
    @Input() tagFilterModeOptions?: readonly TagFilterMode[];

    @Input() dedicatedAdminOptions?: UserInfoListEntry[];
    @Input() selectedDedicatedAdmin: UserInfoListEntry | null = null;
    @Input() dedicatedAdminDisabled: boolean = false;

    @Input() selectionMode: EntitySelectionMode = 'single';

    // Single selection mode
    @Input() selectedEntity?: OrganizationEntityDto;
    @Output() selectedEntityChange = new EventEmitter<OrganizationEntityDto>();

    // Multiple selection mode
    @Input() selectedEntities?: OrganizationEntityDto[];
    @Output() selectedEntitiesChange = new EventEmitter<OrganizationEntityDto[]>();

    // Tag Selection for result page
    @Input() selectedTags?: TreeNode<TagEntitiesDto>[];
    @Output() selectedTagsChange = new EventEmitter<TreeNode<TagEntitiesDto>[]>();
    @Input() selectedTagFilterMode: TagFilterMode | undefined;
    @Output() selectedTagFilterModeChange = new EventEmitter<TagFilterMode>();
    @Input() anyExpertData: boolean = false;

    @Input() selectedYear?: number;
    selectedFilters?: string[];

    // Emitted when the selected year changes (two-way binding support)
    @Output() selectedYearChange = new EventEmitter<number>();
    // Emits the new value when two-way binding is used with [(selectedDedicatedAdmin)]
    @Output() selectedDedicatedAdminChange = new EventEmitter<UserInfoListEntry | null>();
    // Emits the raw change event from the dedicated admin dropdown (for custom logic in parent)
    @Output() dedicatedAdminChange = new EventEmitter<SelectChangeEvent>();

    // === RIGHT SIDE ===
    @Input() showQuestionnaireIcon: boolean = false;
    @Input() knobValue?: number;
    @Input() rightDropdownOptions?: string[];
    @Input() rightDropdownSelected?: string;
    @Input() rightText?: string;

    @Input() showCommentIcon: boolean = false;
    @Input() showAttachmentIcon: boolean = false;

    // Event emitter used to notify the parent component to open a modal dialog.
    @Output() openModal = new EventEmitter<void>();

    /**
     * Emits an event to open a modal.
     * Called when the related UI element (e.g., button) is clicked.
     */
    onOpenModalClick(): void {
        this.openModal.emit();
    }

    /**
     * Handles changes in entity selection based on the current selection mode
     * Emits events to the parent component which will handle the service updates
     */
    handleEntityChange(): void {
        if (this.selectionMode === 'single') {
            // Emit the new value for two-way binding
            this.selectedEntityChange.emit(this.selectedEntity);
        } else {
            // Emit the new value for two-way binding
            this.selectedEntitiesChange.emit(this.selectedEntities);
        }
    }

    /**
     * Handles changes in year selection
     * Emits events to the parent component for two-way binding and custom logic
     */
    handleYearChange(): void {
        // Emit the new value for two-way binding
        this.selectedYearChange.emit(this.selectedYear);
    }

    handleTagChange(): void {
        if (this.selectedTags && Array.isArray(this.selectedTags) && this.selectedTags.length > 1) {
            this.selectedTags.sort((a, b) =>
                (a.label || '').localeCompare(b.label || '', undefined, { numeric: true }),
            );
        }
        this.selectedTagFilterModeChange.emit(this.selectedTagFilterMode);
        this.selectedTagsChange.emit(this.selectedTags);
    }

    /**
     * Removes a specific tag from the selectedTags array
     */
    removeTag(tagToRemove: { key?: string }): void {
        if (this.selectedTags) {
            this.selectedTags = this.selectedTags.filter((tag) => tag.key !== tagToRemove.key);
            this.handleTagChange();
        }
    }

    getTagTooltipText(): string {
        if (this.selectedTags === undefined) {
            return '';
        }
        return this.selectedTags
            .sort((a, b) => (a.label ?? '').localeCompare(b.label ?? ''))
            .slice(2)
            .map((tag) => tag.label + '\n')
            .join('');
    }
}
